import { Icon20Add, Icon20ChevronRight, Icon20UserOutline } from '@vkontakte/icons';
import { Headline, Text, UsersStack } from '@vkontakte/vkui';
import React, { memo, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/blocks/Weblink/WeblinkList/SharingNewWeblinkList.types';
import { DomainAccess } from 'reactApp/components/SharingWindow/Sharing.types';
import type { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { loadUser } from 'reactApp/modules/user/user.thunkActions';
import {
    weblinkAccessControlLoadStart,
    weblinkAccessControlReset,
} from 'reactApp/modules/weblinkAccessControl/weblinkAccessControl.actions';
import { getACL, isAccessControlListLoading } from 'reactApp/modules/weblinkAccessControl/weblinkAccessControl.selectors';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipPlacement } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';

import styles from './SharingOptions.css';

interface Props {
    item?: PublishItem;
    onClick: () => void;
    domainAccess: DomainAccess;
    isEmbedded: boolean;
    variant?: SHARING_VARIANT;
}

const SharingOptionAddPersonalAccess = memo(({ item, onClick, domainAccess, isEmbedded, variant = SHARING_VARIANT.files }: Props) => {
    const personalAccessTooltipRef = useRef<HTMLDivElement>(null);

    const dispatch = useDispatch();

    const acList = useSelector(getACL);
    const isACListLoading = useSelector(isAccessControlListLoading);

    const showPersonalAccessTooltip = useMemo(() => {
        return !isACListLoading && acList.length === 0 && domainAccess === DomainAccess.EMAIL_LIST_ACCESS;
    }, [acList.length, isACListLoading, domainAccess]);

    const { isLoaded, hasError: isUserError } = useSelector(UserSelectors.getLifeCycleState);
    const isUserLoaded = isLoaded && !isUserError;

    useEffect(() => {
        if (!isEmbedded) {
            if (!isUserLoaded) {
                (async () => {
                    await dispatch(loadUser);
                    await dispatch(weblinkAccessControlLoadStart({ id: item?.id || '', storage: item?.storage }));
                })();
            } else {
                dispatch(weblinkAccessControlLoadStart({ id: item?.id || '', storage: item?.storage }));
            }
        }

        return function cleanup() {
            if (!isEmbedded) {
                dispatch(weblinkAccessControlReset());
            }
        };
    }, [dispatch, isEmbedded, isUserLoaded, item?.id, item?.storage]);

    const Label = () => {
        switch (variant) {
            case SHARING_VARIANT.filesBiz:
                return (
                    <>
                        <Icon20UserOutline />
                        Персональный доступ к файлу
                    </>
                );
            case SHARING_VARIANT.foldersBiz:
                return (
                    <>
                        <Icon20UserOutline />
                        Персональный доступ к папке
                    </>
                );
            default:
                return (
                    <>
                        <Icon20Add />
                        Добавить email пользователя
                    </>
                );
        }
    };

    return (
        <>
            <div
                data-qa-id="sharing-option-add-personal-access"
                role="button"
                onClick={onClick}
                className={styles.sharingMoreOptions}
                ref={personalAccessTooltipRef}
            >
                <span className={styles.showMoreTitle}>
                    <Label />
                </span>
                <span className={styles.openPersonalAccessButton}>
                    {variant !== SHARING_VARIANT.foldersBiz && (
                        <UsersStack visibleCount={1} photos={acList.map((item) => item.avatarUrl)} />
                    )}
                    <Icon20ChevronRight className={styles.personalAccessButtonChevron} />
                </span>
            </div>
            {showPersonalAccessTooltip && (
                <FloatingTooltip
                    target={personalAccessTooltipRef}
                    placement={ETooltipPlacement.left}
                    qaId="public-download-disable"
                    withinModal
                    closeOnOutsideClick
                    usePortal
                    closable={false}
                >
                    <div className={styles.tooltipContent}>
                        <Headline weight="1" className={styles.tooltipHeadline}>
                            Добавьте пользователей
                        </Headline>
                        <Text>
                            С текущими настройками файл
                            <br />
                            будет доступен только им
                        </Text>
                    </div>
                </FloatingTooltip>
            )}
        </>
    );
});

SharingOptionAddPersonalAccess.displayName = 'SharingOptionAddPersonalAccess';

export default SharingOptionAddPersonalAccess;
